import { useCallback, useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import { CampaignApplicationRestApi } from "src/types";

import { handleError } from "../../utils/error";
import { useAuthContext } from "../auth";
import { usePaginatedSlice, usePagination } from "../pagination";
import { deserializeGetCampaignApplicationsResponse } from "./protocol.deserializers";

export type UseCampaignApplicationsReturn = ReturnType<typeof useCampaignApplications>;

export const useCampaignApplications = (campaignId: string, returnPaginatedSlice: boolean = true) => {
    const { client } = useAuthContext();

    const [campaignApplications, setCampaignApplications] = useState<CampaignApplicationRestApi[]>([]);
    const [campaignApplicationsUpdateKey, setCampaignApplicationsUpdateKey] = useState("");
    const [isLoadingCampaignApplications, setIsLoadingCampaignApplications] = useState(false);

    const pagination = usePagination({
        mode: "local",
    });

    const fetchCampaignApplications = useCallback(async (campaignId: string, clientId?: string, nextToken?: string) => {
        setIsLoadingCampaignApplications(true);

        let hasNext = false;
        let requestId: string | undefined;
        try {
            const currentSession = await Auth.currentSession();

            const data = await API.get("RestAPI", `/campaigns/${campaignId}/applications`, {
                headers: {
                    Authorization: `Bearer ${currentSession.getIdToken().getJwtToken()}`,
                },
                queryStringParameters: {
                    maxLambdaLimitBytes: 4800000,
                    clientId,
                    nextToken,
                },
            });

            const result = await deserializeGetCampaignApplicationsResponse(data);
            requestId = result.requestId;

            setCampaignApplicationsUpdateKey((updateKey) => `${updateKey}${result.updateKey}`);
            setCampaignApplications((currentCampaignApplications) => {
                const campaignApplications = [...currentCampaignApplications, ...result.campaignApplications];

                pagination.setLocalTotal(campaignApplications?.length);

                return campaignApplications;
            });

            if (result.nextToken) {
                hasNext = true;
                fetchCampaignApplications(campaignId, clientId, result.nextToken);
            }
        } catch (err) {
            handleError(err, {
                message: { key: "campaignapplications_load_failed", namespace: "common" },
                context: { requestId },
            });
        } finally {
            if (!hasNext) {
                setIsLoadingCampaignApplications(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAllCampaignApplications = useCallback((campaignId: string, clientId?: string) => {
        setCampaignApplications([]);
        setCampaignApplicationsUpdateKey("");
        fetchCampaignApplications(campaignId, clientId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!campaignId || !client?.id) {
            return;
        }

        fetchAllCampaignApplications(campaignId, client?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, client?.id]);

    return {
        isLoadingCampaignApplications,
        campaignApplicationsPagination: pagination,
        allCampaignApplications: campaignApplications,
        allCampaignApplicationsUpdateKey: campaignApplicationsUpdateKey,
        campaignApplications: usePaginatedSlice(campaignApplications, pagination, null, !returnPaginatedSlice),
        reloadCampaignApplications() {
            fetchAllCampaignApplications(campaignId);
        },
    };
};
